/**
 * AdminFitnessAddon — Iter 131.
 *
 * Purchase / request the Fitness & Wellness add-on. Two variants:
 *   1. Bundled  — attaches to the clinic's existing subscription
 *                  (single invoice, single billing cycle).
 *   2. Standalone — separate Stripe subscription (independent renewal,
 *                    its own invoice, easy to cancel without touching
 *                    the base plan).
 *
 * When Stripe prices ARE configured (STRIPE_PRICE_FITNESS_*_MONTHLY /
 * _YEARLY env vars), the Buy button opens a Stripe Checkout. When they
 * are NOT configured, the button switches to "Request activation" and
 * files a `fitness_activation_requests` row so the platform team can
 * enable it manually + invoice out-of-band.
 *
 * Mounted at /admin/fitness-addon; visible to clinic owners (require_owner
 * on the backend). Purchase-success + purchase-cancelled query params
 * are read from the URL after Stripe redirects back.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { redirectExternal } from '../../lib/redirect';
import {
  Dumbbell, CheckCircle2, Sparkles, Package, Building2, Loader2,
  ShieldCheck, Mail, ArrowLeft, XCircle, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

export default function AdminFitnessAddon() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalogue, setCatalogue] = useState(null);
  const [state, setState] = useState(null);
  const [billing, setBilling] = useState('monthly');
  const [busyVariant, setBusyVariant] = useState('');
  const [requestModal, setRequestModal] = useState(null);
  const [requestForm, setRequestForm] = useState({
    contact_name: '', contact_phone: '', notes: '',
  });
  const [requestBusy, setRequestBusy] = useState(false);

  // Post-Stripe-checkout redirect banner.
  const purchaseStatus = searchParams.get('purchase');

  const load = () => Promise.all([
    api.get('/fitness/addon/catalogue').then(r => r.data),
    api.get('/clinic/me/fitness/addon').then(r => r.data),
  ]).then(([cat, st]) => {
    setCatalogue(cat);
    setState(st);
  });

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (purchaseStatus === 'success') {
      toast.success('Fitness add-on activated — welcome aboard!', { duration: 8000 });
      // Refresh state after a short delay to give the webhook a moment.
      setTimeout(load, 1500);
      // Clean the URL so a page refresh doesn't re-fire the toast.
      searchParams.delete('purchase');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
    } else if (purchaseStatus === 'cancelled') {
      toast.message('Checkout cancelled — no charge was made.');
      searchParams.delete('purchase');
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseStatus]);

  const startCheckout = async (variant) => {
    setBusyVariant(variant);
    try {
      const { data } = await api.post('/clinic/me/fitness/addon/checkout', {
        variant,
        billing,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        redirectExternal(data.checkout_url);
      }
    } catch (e) {
      // Iter 131 — Structured error surfaces the "no Stripe price wired yet"
      // case so we can gracefully fall back to a manual activation request.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'price_not_configured') {
        setRequestModal({ variant, billing });
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || e.message);
      }
    } finally {
      setBusyVariant('');
    }
  };

  const submitRequest = async () => {
    if (!requestModal) return;
    setRequestBusy(true);
    try {
      await api.post('/clinic/me/fitness/addon/request', {
        variant: requestModal.variant,
        billing: requestModal.billing,
        contact_name: requestForm.contact_name || null,
        contact_phone: requestForm.contact_phone || null,
        notes: requestForm.notes || null,
      });
      toast.success(
        'Request sent. Our team will reach out with the invoice + activation link.',
        { duration: 8000 },
      );
      setRequestModal(null);
      setRequestForm({ contact_name: '', contact_phone: '', notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setRequestBusy(false);
    }
  };

  if (!catalogue || !state) {
    return <div className="p-8 text-fg-muted flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin"/> Loading…
    </div>;
  }

  const activeVariant = state.addon?.variant;
  const activeStatus = state.addon?.status;
  const isActive = state.feature_fitness && activeStatus === 'active';
  const isRequested = activeStatus === 'requested';

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">ADD-ON MODULE</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
          <Dumbbell className="w-8 h-8 text-brand"/> Fitness &amp; wellness add-on
        </h1>
        <p className="text-fg-muted mt-3 max-w-3xl">
          The Fitness module is a paid add-on kept strictly separate from clinical care
          (own client model, own waiver of liability, own invoicing stream — never
          billed as physiotherapy). Pick the variant that fits your billing preference.
        </p>
      </div>

      {isActive && <ActiveBanner state={state} onReload={load}/>}
      {isRequested && !isActive && <RequestedBanner state={state}/>}

      {/* Billing cycle toggle */}
      <div className="flex items-center gap-2 mb-5" data-testid="billing-toggle">
        <BillingChip label="Monthly" active={billing==='monthly'} onClick={()=>setBilling('monthly')} testid="billing-monthly"/>
        <BillingChip label="Yearly · save 17%" active={billing==='yearly'} onClick={()=>setBilling('yearly')} testid="billing-yearly"/>
      </div>

      {/* Two variant cards */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="fitness-variants">
        {catalogue.variants.map((v) => (
          <VariantCard
            key={v.id}
            variant={v}
            billing={billing}
            currency={catalogue.currency}
            currentlyActive={isActive && activeVariant === v.id}
            busy={busyVariant === v.id}
            onBuy={() => startCheckout(v.id)}
            onRequest={() => setRequestModal({ variant: v.id, billing })}
          />
        ))}
      </div>

      {/* FAQ */}
      <div className="mt-8 surface border border-soft rounded-lg p-5 text-sm text-fg-secondary">
        <div className="font-heading font-semibold mb-3 inline-flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand"/> Frequently asked
        </div>
        <div className="space-y-3">
          <FAQ q="Why is Fitness a paid add-on now?"
               a="Fitness runs on completely separate compliance rails (waiver of liability, non-clinical GDPR basis, separate invoicing). We charge for it because it's not part of the standard clinical practice tooling — only clinics that actively offer wellness training need it."/>
          <FAQ q="What happens if I cancel the add-on?"
               a="Your existing fitness data (clients, memberships, invoices) is preserved but read-only. The dashboard and booking flows disappear. Reactivating anytime restores full access."/>
          <FAQ q="Bundled vs standalone — which should I pick?"
               a="Bundled (€29/mo) is cheaper and appears on the same invoice as your clinic subscription. Standalone (€39/mo) has its own invoice + own cancellation cycle — better if the fitness P&L is tracked separately."/>
          <FAQ q="Can I pilot before committing?"
               a="Yes — pick standalone monthly. You can cancel next month with zero impact on your clinic subscription."/>
        </div>
      </div>

      {/* Manual-request modal */}
      {requestModal && (
        <RequestModal
          variant={requestModal.variant}
          billing={requestModal.billing}
          form={requestForm}
          onChange={(k,v) => setRequestForm(f => ({...f, [k]: v}))}
          onSubmit={submitRequest}
          onClose={() => setRequestModal(null)}
          busy={requestBusy}
          variantMeta={catalogue.variants.find(v => v.id === requestModal.variant)}
        />
      )}
    </div>
  );
}

function VariantCard({ variant: v, billing, currency, currentlyActive, busy, onBuy, onRequest }) {
  const price = billing === 'monthly' ? v.price_monthly_eur : v.price_yearly_eur;
  const period = billing === 'monthly' ? '/mo' : '/yr';
  const badge = v.id === 'bundled'
    ? { label: 'Most popular', icon: Sparkles, color: 'bg-brand text-white' }
    : { label: 'Independent', icon: Package, color: 'bg-[#F2EBDB] text-[#8A5A00]' };
  const Icon = badge.icon;

  return (
    <div
      className={`rounded-xl border-2 p-5 transition ${
        currentlyActive
          ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/30'
          : 'border-soft hover:border-brand/40'
      }`}
      data-testid={`fitness-variant-${v.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-11 h-11 rounded-lg bg-brand/10 grid place-items-center shrink-0">
          {v.id === 'bundled' ? <Building2 className="w-5 h-5 text-brand"/> : <Package className="w-5 h-5 text-brand"/>}
        </div>
        <div className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold inline-flex items-center gap-1 ${badge.color}`}>
          <Icon className="w-3 h-3"/> {badge.label}
        </div>
      </div>
      <h2 className="font-heading font-bold text-lg mb-1">{v.label.replace('Fitness add-on — ', '')}</h2>
      <p className="text-xs text-fg-muted leading-relaxed min-h-[36px]">{v.tagline}</p>
      <div className="mt-4 mb-3 flex items-baseline gap-1">
        <span className="text-3xl font-heading font-bold text-fg">
          {currency === 'EUR' ? '€' : currency + ' '}{price}
        </span>
        <span className="text-sm text-fg-muted">{period}</span>
      </div>
      <ul className="space-y-1.5 mb-5">
        {v.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-fg-secondary">
            <CheckCircle2 className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0"/>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="text-[11px] text-fg-muted mb-4 italic border-l-2 border-brand/40 pl-2">
        {v.recommended_for}
      </div>

      {currentlyActive ? (
        <div className="w-full text-center text-sm font-semibold text-emerald-700 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-md bg-emerald-50 border border-emerald-200">
          <CheckCircle2 className="w-4 h-4"/> Active — you're using this
        </div>
      ) : v.billing_cycles?.[billing] ? (
        <button
          type="button"
          onClick={onBuy}
          disabled={busy}
          className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid={`fitness-buy-${v.id}`}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
          {busy ? 'Redirecting…' : `Purchase ${v.id}`}
        </button>
      ) : (
        <button
          type="button"
          onClick={onRequest}
          className="btn-secondary w-full inline-flex items-center justify-center gap-2"
          data-testid={`fitness-request-${v.id}`}
          title="Stripe price not configured — the platform team will activate manually and invoice."
        >
          <Mail className="w-4 h-4"/> Request activation
        </button>
      )}
    </div>
  );
}

function BillingChip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
        active ? 'bg-brand text-white' : 'bg-[#F2EBDB] text-[#4A524A] hover:bg-[#e7dcc4]'
      }`}
    >
      {label}
    </button>
  );
}

function ActiveBanner({ state, onReload }) {
  return (
    <div
      className="mb-6 p-4 rounded-lg border border-emerald-300 bg-emerald-50 flex items-start gap-3"
      data-testid="fitness-active-banner"
    >
      <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0"/>
      <div className="flex-1 text-sm">
        <div className="font-heading font-semibold text-emerald-900">
          Fitness &amp; wellness is active on your clinic
          <span className="text-xs font-normal text-emerald-700 ml-2">
            · variant: <strong className="capitalize">{state.addon?.variant || '—'}</strong>
            {state.addon?.billing && <> · {state.addon.billing}</>}
          </span>
        </div>
        <div className="text-emerald-800/80 mt-1 text-xs">
          Head over to <Link to="/admin/fitness" className="underline font-medium">Fitness dashboard</Link> to
          onboard your first client. To cancel, open <Link to="/admin/billing" className="underline font-medium">Billing → Manage subscription</Link>.
        </div>
      </div>
      <button
        type="button"
        onClick={onReload}
        className="text-xs text-emerald-800 underline hover:text-emerald-900 shrink-0"
        data-testid="fitness-reload-state"
      >
        Refresh
      </button>
    </div>
  );
}

function RequestedBanner({ state }) {
  return (
    <div
      className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-3"
      data-testid="fitness-requested-banner"
    >
      <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 shrink-0"/>
      <div className="flex-1 text-sm">
        <div className="font-heading font-semibold text-amber-900">
          Activation request pending — variant <strong className="capitalize">{state.addon?.variant}</strong>
        </div>
        <div className="text-amber-800/90 mt-1 text-xs leading-relaxed">
          Our team received your request and will reach out shortly with the
          invoice + activation link. In the meantime you can still browse the
          catalogue below and change your variant preference.
        </div>
      </div>
    </div>
  );
}

function FAQ({ q, a }) {
  return (
    <details className="group">
      <summary className="cursor-pointer text-sm font-medium text-fg hover:text-brand list-none flex items-center gap-2">
        <span className="text-brand group-open:rotate-90 transition inline-block">▸</span>
        {q}
      </summary>
      <div className="text-xs text-fg-muted mt-1.5 leading-relaxed pl-4">{a}</div>
    </details>
  );
}

function RequestModal({ variant, billing, form, onChange, onSubmit, onClose, busy, variantMeta }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="fitness-request-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-fg-muted hover:text-fg" data-testid="fitness-request-close">
          <XCircle className="w-5 h-5"/>
        </button>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-brand/10 grid place-items-center shrink-0">
            <Dumbbell className="w-5 h-5 text-brand"/>
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold">Request Fitness activation</h2>
            <div className="text-[11px] uppercase tracking-wider text-fg-muted mt-0.5">
              {variantMeta?.label} · {billing}
            </div>
          </div>
        </div>
        <p className="text-xs text-fg-muted mb-4">
          Stripe checkout for this variant isn&apos;t wired up on this environment yet.
          Send us a quick request and the platform team will activate the module
          manually + email you an invoice within one business day.
        </p>

        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-fg-muted font-semibold">Best contact name</span>
            <input type="text" className="input mt-1.5 w-full text-sm"
                   value={form.contact_name} onChange={e => onChange('contact_name', e.target.value)}
                   placeholder="Dr. Jane Doe" data-testid="fitness-request-name"/>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-fg-muted font-semibold">Contact phone (optional)</span>
            <input type="tel" className="input mt-1.5 w-full text-sm"
                   value={form.contact_phone} onChange={e => onChange('contact_phone', e.target.value)}
                   placeholder="+48 …" data-testid="fitness-request-phone"/>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-fg-muted font-semibold">Notes (optional)</span>
            <textarea rows={3} className="input mt-1.5 w-full text-sm min-h-[70px]"
                      value={form.notes} onChange={e => onChange('notes', e.target.value)}
                      placeholder="Anything we should know before activating?"
                      data-testid="fitness-request-notes"/>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="btn-secondary text-sm" data-testid="fitness-request-cancel">
            <ArrowLeft className="w-3.5 h-3.5 inline mr-1"/> Not now
          </button>
          <button type="button" onClick={onSubmit} disabled={busy}
                  className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="fitness-request-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
            Send request
          </button>
        </div>
      </div>
    </div>
  );
}
